import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
} from '@mui/material';
import {
  CheckCircleOutline as CheckCircleIcon,
  ArrowForward as ArrowIcon,
  Factory as FactoryIcon,
  LocalShipping as ShippingIcon,
  Science as ScienceIcon,
  Agriculture as AgricultureIcon,
  Warehouse as WarehouseIcon,
  Bolt as EnergyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Seo, { SITE_URL } from '../components/Seo';
import { trackEvent } from '../utils/analytics';
import { services } from '../data/services';

const industries = [
  { label: 'Manufacturing & Engineering', icon: <FactoryIcon /> },
  { label: 'Shipping & Logistics', icon: <ShippingIcon /> },
  { label: 'Chemicals & Petrochemicals', icon: <ScienceIcon /> },
  { label: 'Agriculture & Commodities', icon: <AgricultureIcon /> },
  { label: 'Warehousing & Storage', icon: <WarehouseIcon /> },
  { label: 'Energy & Minerals', icon: <EnergyIcon /> },
];

const process = [
  {
    step: '01',
    title: 'Scope & Booking',
    description: 'You share the cargo, vessel and location details. We confirm scope, standards and timing.',
  },
  {
    step: '02',
    title: 'On-Site Inspection',
    description: 'A certified surveyor attends the port, vessel or facility and carries out the agreed checks.',
  },
  {
    step: '03',
    title: 'Verification',
    description: 'Findings are cross-checked against specifications, documents and applicable standards.',
  },
  {
    step: '04',
    title: 'Reporting',
    description: 'You receive a clear, photo-supported report you can act on or share with your counterparties.',
  },
];

const SectionLabel = ({ children }) => (
  <Typography variant="overline" sx={{ color: 'secondary.dark', display: 'block', mb: 1 }}>
    {children}
  </Typography>
);

const Services = () => {
  const navigate = useNavigate();

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'InspectIQ Inspection Services',
    itemListElement: services.map((s, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: s.title,
      url: `${SITE_URL}/services/${s.slug}`,
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Services', item: `${SITE_URL}/services` },
    ],
  };

  return (
    <Box>
      <Seo
        title="Inspection & Survey Services"
        description="Marine & draft surveys, general & liquid cargo, container inspections and project cargo supervision. Independent third-party inspection services at ports worldwide."
        path="/services"
        structuredData={[itemListSchema, breadcrumbSchema]}
      />
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <SectionLabel>Our Services</SectionLabel>
          <Typography variant="h2" component="h1" gutterBottom>
            Independent inspection and survey, end to end
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 760, mx: 'auto' }}>
            From the berth to the warehouse, InspectIQ provides third-party inspection
            and survey services that verify the condition, quality and quantity of cargo
            at ports and facilities across India and around the world.
          </Typography>
        </Box>

        {/* Services Grid */}
        <Grid container spacing={4}>
          {services.map((service) => (
            <Grid item xs={12} md={6} key={service.slug}>
              <Card
                onClick={() => {
                  trackEvent('select_service', { service: service.slug, location: 'services_list' });
                  navigate(`/services/${service.slug}`);
                }}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  borderTop: '3px solid',
                  borderTopColor: 'secondary.main',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 18px 40px rgba(15, 39, 51, 0.12)',
                  },
                }}
              >
                <CardMedia
                  component="img"
                  image={service.image}
                  alt={service.title}
                  sx={{ height: 200, objectFit: 'cover' }}
                />
                <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {service.icon}
                    <Typography variant="h5" component="h2" sx={{ ml: 2, color: 'text.primary' }}>
                      {service.title}
                    </Typography>
                  </Box>

                  <Typography variant="body1" paragraph>
                    {service.shortDescription}
                  </Typography>

                  <List sx={{ mb: 1 }}>
                    {service.included.slice(0, 3).map((feature, featureIndex) => (
                      <ListItem key={featureIndex} sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{ color: 'text.primary', variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Box sx={{ mt: 'auto' }}>
                    <Button
                      endIcon={<ArrowIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        trackEvent('select_service', { service: service.slug, location: 'services_list_button' });
                        navigate(`/services/${service.slug}`);
                      }}
                      sx={{ px: 0, '&:hover': { backgroundColor: 'transparent' } }}
                    >
                      View service details
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Industries We Serve */}
      <Box sx={{ bgcolor: '#f5f8fa', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <SectionLabel>Industries We Serve</SectionLabel>
            <Typography variant="h2" gutterBottom>
              Sector expertise across the supply chain
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 680, mx: 'auto' }}>
              Our surveyors work across the industries that move cargo through India's
              ports and trade corridors.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {industries.map((industry, index) => (
              <Grid item xs={6} md={4} key={index}>
                <Card sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: 'rgba(1, 86, 122, 0.08)',
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {industry.icon}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {industry.label}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How We Work */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <SectionLabel>How We Work</SectionLabel>
          <Typography variant="h2" gutterBottom>
            A clear process, from booking to report
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {process.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box sx={{ height: '100%' }}>
                <Typography
                  sx={{
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    fontWeight: 800,
                    fontSize: '2.5rem',
                    color: 'secondary.main',
                    lineHeight: 1,
                    mb: 1.5,
                  }}
                >
                  {item.step}
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Standards & Compliance */}
      <Box sx={{ bgcolor: 'primary.dark', color: 'white', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="overline" sx={{ color: 'secondary.light', display: 'block', mb: 1 }}>
                Standards & Compliance
              </Typography>
              <Typography variant="h3" gutterBottom sx={{ color: 'white' }}>
                Inspections aligned with international standards
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                Our work follows recognised inspection and survey practices, and our team
                includes a certified ISO auditor. As an independent third party, our
                findings stay objective and free of commercial influence.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { md: 'right' } }}>
              <Stack direction="row" spacing={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <CheckCircleIcon sx={{ fontSize: 40, color: 'secondary.light' }} />
              </Stack>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
                Independent · Objective · Traceable
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Call to Action */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 9 } }}>
        <Box
          sx={{
            textAlign: 'center',
            bgcolor: '#f5f8fa',
            borderRadius: 4,
            py: { xs: 5, md: 7 },
            px: 3,
          }}
        >
          <Typography variant="h4" gutterBottom>
            Not sure which inspection you need?
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 560, mx: 'auto', mb: 3 }}>
            Share a few details about your cargo or vessel and we'll recommend the right
            scope and provide a quote.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => navigate('/quote')}
            sx={{ px: 4, py: 1.5 }}
          >
            Request a Quote
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Services;
